// src/App.js - VERSION CORRIGÉE

import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import LoginQRClone from './components/LoginQRClone';
import ErrorBoundary from './components/ErrorBoundary';
import AuthenticatedRoutes from './AuthenticatedRoutes';
import { MissionProvider } from './context/MissionContext';
import cloneManager from './services/cloneManager';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cloneInfo, setCloneInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);

  const checkForUpdate = async () => {
    if (process.env.NODE_ENV !== 'production') return;
    try {
      const response = await fetch('/version.json', { cache: 'no-store' });
      if (!response.ok) return;
      const payload = await response.json();
      if (!payload?.version) return;

      const storedVersion = localStorage.getItem('rapteck_pwa_version');
      if (!storedVersion) {
        localStorage.setItem('rapteck_pwa_version', payload.version);
        return;
      }

      if (storedVersion !== payload.version) {
        setLatestVersion(payload.version);
        setUpdateAvailable(true);
      }
    } catch (error) {
      console.warn('⚠️ Impossible de vérifier la version:', error?.message || error);
    }
  };

  const handleUpdateNow = async () => {
    try {
      if (latestVersion) {
        localStorage.setItem('rapteck_pwa_version', latestVersion);
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch (error) {
      console.warn('⚠️ Erreur purge cache:', error?.message || error);
    } finally {
      window.location.reload();
    }
  };

  useEffect(() => {
    // Vérifie si un clone est déjà configuré au démarrage
    const initAuth = async () => {
      try {
        console.log('🔍 ========================================');
        console.log('🔍 INITIALISATION APP');
        console.log('🔍 ========================================');
        
        const clone = cloneManager.getClone();
        
        console.log('🔍 Clone récupéré:', clone);
        console.log('🔍 Clone présent:', !!clone);
        console.log('🔍 Token présent:', !!clone?.token);
        console.log('🔍 Inspecteur présent:', !!clone?.inspecteur);
        
        if (clone && clone.token) {
          console.log('✅ Clone trouvé en cache:', clone.clone_name);
          console.log('✅ API URL:', clone.api_url);
          console.log('✅ Token:', clone.token.substring(0, 15) + '...');
          console.log('✅ Inspecteur:', clone.inspecteur?.prenom, clone.inspecteur?.nom);
          
          setCloneInfo(clone);
          setIsAuthenticated(true);
        } else {
          console.log('ℹ️ Aucune session sauvegardée');
          console.log('ℹ️ Raison:', !clone ? 'Pas de clone' : 'Pas de token');
        }
      } catch (error) {
        console.error('❌ Erreur init auth:', error);
      } finally {
        console.log('🔍 Fin initialisation - isAuthenticated:', isAuthenticated);
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    checkForUpdate();
    const interval = setInterval(checkForUpdate, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLoginSuccess = (info) => {
    console.log('🔍 ========================================');
    console.log('🔍 LOGIN SUCCESS');
    console.log('🔍 ========================================');
    console.log('✅ Login réussi - Clone:', info.clone_name);
    console.log('✅ Info reçue:', info);
    
    // Recharger les infos depuis cloneManager pour être sûr
    const clone = cloneManager.getClone();
    console.log('✅ Clone après login:', clone);
    
    setCloneInfo(clone);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    console.log('🔍 ========================================');
    console.log('🔍 LOGOUT');
    console.log('🔍 ========================================');
    console.log('🚪 Déconnexion...');
    
    // Nettoie tout via cloneManager
    cloneManager.logout();
    
    console.log('✅ CloneManager nettoyé');
    console.log('✅ LocalStorage vidé');
    
    // Réinitialise l'état
    setIsAuthenticated(false);
    setCloneInfo(null);
    
    console.log('✅ Déconnexion terminée');
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
        background: 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)'
      }}>
        <div className="spinner-large"></div>
        <p style={{ color: '#6b7280', fontSize: '16px' }}>Chargement...</p>
      </div>
    );
  }

  const updateBanner = updateAvailable ? (
    <div style={{
      position: 'fixed',
      top: 10,
      left: 10,
      right: 10,
      zIndex: 9999,
      background: '#1f2937',
      color: '#fff',
      padding: '10px 12px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
    }}>
      <span style={{ fontSize: '13px' }}>
        ✅ Une mise à jour est disponible{latestVersion ? ` (v${latestVersion})` : ''}.
      </span>
      <button
        onClick={handleUpdateNow}
        style={{
          background: '#f59e0b',
          color: '#111827',
          border: 'none',
          padding: '6px 10px',
          borderRadius: '8px',
          fontWeight: '700',
          cursor: 'pointer'
        }}
      >
        Mettre à jour
      </button>
    </div>
  ) : null;

  // Si pas connecté, affiche le Login QR
  if (!isAuthenticated) {
    return (
      <>
        {updateBanner}
        <LoginQRClone onLoginSuccess={handleLoginSuccess} />
      </>
    );
  }

  // Si connecté, affiche Home avec les infos du clone
  if (!cloneInfo?.inspecteur) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#f8fafc',
        padding: '24px',
        textAlign: 'center'
      }}>
        {updateBanner}
        <h2 style={{ fontSize: '20px', color: '#0f172a' }}>Session incomplète</h2>
        <p style={{ color: '#475569', maxWidth: '520px' }}>
          L’inspecteur n’est pas disponible dans la session. Réinitialise puis rescane le QR code.
        </p>
        <button
          onClick={handleLogout}
          style={{
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            padding: '10px 16px',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Réinitialiser la session
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary onReset={handleLogout}>
      {updateBanner}
      <BrowserRouter>
        <MissionProvider user={cloneInfo?.inspecteur} onLogout={handleLogout}>
          <AuthenticatedRoutes />
        </MissionProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;